import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  reportsApi,
  type SalesAnalysisGoods,
  type SalesAnalysisRowInput,
} from '../../api/reports.js';
import { Field } from '../../components/ui.js';
import { ReportBody } from './ReportPage.js';

/**
 * 정의 기반 리포트 sales-analysis(이벤트 판매추이 분석) 위에, 원본
 * management/product/sales_analysis_list.php 의 행마다 있던 관리 버튼
 * (수정 · 복사 · 삭제)을 얹은 화면. 표·필터·정렬·CSV·진행중/종료 나누기는
 * ReportBody 가 그대로 그린다 - zaiko-stock 과 같은 방식이다.
 *
 * 원본은 버튼마다 iframe 모달로 PHP 를 띄우고(prize_modify.php ·
 * prize_copy.php), 저장하면 parent.document.location.reload() 로 목록을
 * 통째로 새로 그렸다. 여기서는 같은 화면 안의 모달에서 고치고, 저장되면
 * 그 표의 조회만 다시 돌린다 - 검색어·정렬·스크롤이 그대로 남는다.
 *
 * 상단의 '+ 상품 추가'(원본 sales_analysis_add.php)도 여기 있다 - 상품코드로
 * 찾아(sales_analysis_add_ajax.php) 옵션을 골라 한 번에 등록한다.
 */
const EVENT_TYPES = [
  { value: 0, label: '지정 안 함' },
  { value: 1, label: '나홀로 오나홀' },
  { value: 2, label: '생애 첫 바이브' },
];

type Row = Record<string, unknown>;

export function SalesAnalysisPage() {
  const queryClient = useQueryClient();
  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; row: Row } | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [adding, setAdding] = useState(false);

  function refresh() {
    // ReportBody 의 조회 키가 ['report', 'sales-analysis', 조건] 이라 앞부분만
    // 맞춰 무효화하면 지금 걸린 조건 그대로 다시 받아온다.
    void queryClient.invalidateQueries({ queryKey: ['report', 'sales-analysis'] });
  }

  return (
    <>
      <ReportBody
        reportId="sales-analysis"
        headerExtra={
          <div className="toolbar">
            <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)}>
              + 상품 추가
            </button>
          </div>
        }
        extraColumn={{
          label: '관리',
          render: (row) => {
            // 수정·삭제는 행의 기본키(seq)가 있어야 부를 수 있다. 서버가 그
            // 값을 안 내려주면 조용히 빈 요청을 보내는 대신 버튼을 잠근다
            // (실제로 투영이 seq 를 지워 두 기능이 안 됐던 적이 있다 -
            // report.engine.ts 의 visibleKeys 주석 참고).
            const hasSeq = Number.isFinite(Number(row.seq));
            return (
            <div className="sales-analysis-actions">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!hasSeq}
                title={hasSeq ? undefined : '이 행의 seq 를 못 받아 수정할 수 없습니다.'}
                onClick={() => setFormModal({ mode: 'edit', row })}
              >
                수정
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm sales-analysis-actions__copy"
                onClick={() => setFormModal({ mode: 'copy', row })}
              >
                복사
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm sales-analysis-actions__delete"
                disabled={!hasSeq}
                title={hasSeq ? undefined : '이 행의 seq 를 못 받아 삭제할 수 없습니다.'}
                onClick={() => setDeleting(row)}
              >
                삭제
              </button>
            </div>
            );
          },
        }}
      />

      {adding && (
        // 등록 결과(몇 개 들어갔고 몇 개를 건너뛰었는지)를 모달 안에서 보여주고,
        // 이어서 다른 상품도 추가할 수 있게 저장했다고 닫지는 않는다 - 표만
        // 다시 받는다. 닫는 건 사용자가 한다.
        <AddProductModal onClose={() => setAdding(false)} onAdded={refresh} />
      )}

      {formModal && (
        <SalesAnalysisFormModal
          mode={formModal.mode}
          row={formModal.row}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}

      {deleting && (
        <DeleteConfirmModal
          row={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

/** 행 값을 폼 상태로. 표에 있는 값을 그대로 쓴다 - 행 하나를 다시 조회하지 않는다. */
type FormState = {
  [K in keyof SalesAnalysisRowInput]: string;
};

function str(v: unknown): string {
  return v === null || v === undefined ? '' : String(v);
}

/** DATE/DATETIME 값에서 <input type="date"> 가 받는 'YYYY-MM-DD' 만 뽑는다. */
function dateInput(v: unknown): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(str(v));
  // 0000-00-00 은 날짜가 아니라 '없음' 이다 (원본 데이터에 그대로 남아 있다).
  return m && m[1] !== '0000-00-00' ? m[1]! : '';
}

function toFormState(row: Row, mode: 'edit' | 'copy'): FormState {
  return {
    event_type: str(row.event_type ?? 0),
    goods_idx: str(row.goods_idx),
    barcode: str(row.barcode),
    thumbnail: str(row.thumbnail),
    gname: str(row.gname),
    op_code: str(row.op_code),
    op_name: str(row.op_name),
    daccount: str(row.daccount),
    account: str(row.account),
    quantity: str(row.quantity),
    memo: str(row.memo),
    open_date: dateInput(row.open_date),
    // 복사는 '같은 상품을 다시 판다' 는 뜻이라 끝난 이벤트를 복사해도 새 행은
    // 진행중으로 시작한다 (원본 prize_copy.php 도 end_flag 를 안 보냈다).
    end_flag: mode === 'copy' ? '0' : str(row.end_flag) === '1' ? '1' : '0',
    end_date: mode === 'copy' ? '' : dateInput(row.end_date),
  };
}

function toInput(form: FormState): SalesAnalysisRowInput {
  return {
    event_type: Number(form.event_type) || 0,
    goods_idx: form.goods_idx.trim(),
    barcode: form.barcode.trim(),
    thumbnail: form.thumbnail.trim(),
    gname: form.gname.trim(),
    op_code: form.op_code.trim(),
    op_name: form.op_name.trim(),
    daccount: Number(form.daccount) || 0,
    account: Number(form.account) || 0,
    quantity: Number(form.quantity) || 0,
    memo: form.memo,
    open_date: form.open_date,
    end_flag: form.end_flag === '1' ? 1 : 0,
    end_date: form.end_flag === '1' ? form.end_date : '',
  };
}

/** 수정 · 복사 모달. 원본 prize_modify.php · prize_copy.php 를 합쳤다. */
function SalesAnalysisFormModal({
  mode,
  row,
  onClose,
  onSaved,
}: {
  mode: 'edit' | 'copy';
  row: Row;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => toFormState(row, mode));
  const seq = Number(row.seq);

  const save = useMutation({
    mutationFn: () => {
      const body = toInput(form);
      return mode === 'edit'
        ? reportsApi.updateSalesAnalysisRow(seq, body)
        : reportsApi.createSalesAnalysisRow(body);
    },
    onSuccess: onSaved,
  });

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // 원본에는 필수 표시가 없었지만, 상품명도 옵션명도 없는 행은 표에서 어느
  // 상품인지 알아볼 수가 없다 - 둘 중 하나는 받는다.
  const valid = form.gname.trim() !== '' || form.op_name.trim() !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '이벤트 상품 수정' : '이벤트 상품 복사'}</h2>

        {mode === 'copy' && (
          <p className="hint">
            <b>{str(row.gname) || str(row.op_name) || '이 행'}</b>의 내용을 그대로 새 행으로
            등록합니다. 보통 옵션(옵션코드·옵션명)만 바꿔 같은 상품의 다른 옵션을 추가합니다.
          </p>
        )}

        <form
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          <Field label="이벤트 종류">
            <select
              className="input"
              value={form.event_type}
              onChange={(e) => set('event_type', e.target.value)}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>

          <div className="row-2">
            <Field label="상품코드">
              <input
                className="input"
                value={form.goods_idx}
                placeholder="예: 2493083"
                onChange={(e) => set('goods_idx', e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Field>
            <Field label="상품 바코드">
              <input className="input" value={form.barcode} onChange={(e) => set('barcode', e.target.value)} />
            </Field>
          </div>

          <Field label="상품명">
            <input className="input" value={form.gname} onChange={(e) => set('gname', e.target.value)} />
          </Field>

          <Field label="이미지 주소">
            <input
              className="input"
              value={form.thumbnail}
              placeholder="https://cdn-banana.bizhost.kr/files/goods/..."
              onChange={(e) => set('thumbnail', e.target.value)}
            />
          </Field>

          <div className="row-2">
            <Field label="옵션코드">
              <input className="input" value={form.op_code} onChange={(e) => set('op_code', e.target.value)} />
            </Field>
            <Field label="옵션명">
              <input className="input" value={form.op_name} onChange={(e) => set('op_name', e.target.value)} />
            </Field>
          </div>

          <div className="row-3">
            <Field label="도매가">
              <input
                className="input"
                type="number"
                min={0}
                value={form.daccount}
                onChange={(e) => set('daccount', e.target.value)}
              />
            </Field>
            <Field label="판매가">
              <input
                className="input"
                type="number"
                min={0}
                value={form.account}
                onChange={(e) => set('account', e.target.value)}
              />
            </Field>
            <Field label="초기 재고">
              <input
                className="input"
                type="number"
                min={0}
                value={form.quantity}
                onChange={(e) => set('quantity', e.target.value)}
              />
            </Field>
          </div>

          <div className="row-2">
            <Field label="판매시작일">
              <input
                className="input"
                type="date"
                value={form.open_date}
                onChange={(e) => set('open_date', e.target.value)}
              />
            </Field>
            <Field label="종료 이벤트" as="div">
              <div className="sales-analysis-form__end">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.end_flag === '1'}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        end_flag: e.target.checked ? '1' : '0',
                        // 원본(prize_modify.php:157)처럼 체크하는 순간 종료일이
                        // 비어 있으면 오늘로 채워둔다.
                        end_date:
                          e.target.checked && f.end_date === ''
                            ? new Date().toISOString().slice(0, 10)
                            : f.end_date,
                      }))
                    }
                  />
                  종료됨
                </label>
                {form.end_flag === '1' && (
                  <input
                    className="input"
                    type="date"
                    aria-label="종료일"
                    value={form.end_date}
                    onChange={(e) => set('end_date', e.target.value)}
                  />
                )}
              </div>
            </Field>
          </div>

          <Field label="메모">
            <textarea
              className="input"
              rows={4}
              value={form.memo}
              onChange={(e) => set('memo', e.target.value)}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof Error ? save.error.message : '저장하지 못했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '수정하기' : '복사하기'}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * 삭제 확인. 원본 prize_modify.php?mode=delete 도 지우기 전에 옵션명을 보여주고
 * 한 번 더 물었다 - 같은 상품의 옵션 행이 여러 개라 어느 줄인지 확인이 필요하다.
 */
function DeleteConfirmModal({
  row,
  onClose,
  onDeleted,
}: {
  row: Row;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => reportsApi.deleteSalesAnalysisRow(Number(row.seq)),
    onSuccess: onDeleted,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">삭제하시겠습니까?</h2>

        <div className="form-summary">
          <div className="form-summary__main">{str(row.gname) || '-'}</div>
          <div className="form-summary__sub">옵션명: {str(row.op_name) || '-'}</div>
        </div>

        <p className="hint">지운 행은 되돌릴 수 없습니다.</p>

        {remove.isError && (
          <div className="qty-msg qty-msg--error">
            {remove.error instanceof Error ? remove.error.message : '삭제하지 못했습니다.'}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? '삭제 중…' : '삭제하기'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * '+ 상품 추가' 모달. 원본 sales_analysis_add.php + sales_analysis_add_ajax.php.
 *
 * 원본 흐름 그대로다: 상품코드로 찾으면 그 상품의 바코드(옵션) 줄이 전부 나오고,
 * 체크한 옵션마다 초기 재고를 정해 한 번에 등록한다. 상품명·판매시작일·메모는
 * 모든 행이 공유한다.
 *
 * 원본과 다른 점 하나: 이미 등록해 둔 옵션은 '등록됨' 으로 표시하고 기본
 * 체크를 꺼둔다. 원본은 유니크 인덱스에 기대어 중복을 걸러내려 했는데 그
 * 인덱스가 없어서, 같은 옵션이 몇 번이고 다시 들어갈 수 있었다.
 */
function AddProductModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [draft, setDraft] = useState('');
  const [goodsIdx, setGoodsIdx] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [openDate, setOpenDate] = useState('');
  const [memo, setMemo] = useState('');
  const [result, setResult] = useState<{ inserted: number; skipped: number } | null>(null);

  const goods = useQuery({
    queryKey: ['report', 'sales-analysis', 'goods', goodsIdx],
    queryFn: () => reportsApi.salesAnalysisGoods(goodsIdx),
    enabled: goodsIdx !== '',
  });

  function search() {
    const idx = draft.trim();
    if (idx === '') return;
    setResult(null);
    setChecked({});
    setQuantities({});
    setGoodsIdx(idx);
  }

  // 조회 결과가 오면 원본처럼 전부 체크해 둔다 (이미 등록된 것만 빼고).
  const data: SalesAnalysisGoods | undefined = goods.data;
  const optionKey = (o: { barcode: string; opCode: string }) => `${o.barcode}|${o.opCode}`;
  function isChecked(o: { barcode: string; opCode: string; registered: boolean }) {
    return checked[optionKey(o)] ?? !o.registered;
  }
  function quantityOf(o: { barcode: string; opCode: string }) {
    return quantities[optionKey(o)] ?? '1';
  }

  const selected = (data?.options ?? []).filter(isChecked);

  const save = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('먼저 상품을 찾아주세요.');
      const rows: SalesAnalysisRowInput[] = selected.map((o) => ({
        event_type: data.eventType,
        goods_idx: data.goodsIdx,
        barcode: o.barcode,
        thumbnail: o.thumbnail,
        gname: data.gname,
        op_code: o.opCode,
        op_name: o.opName,
        daccount: o.daccount,
        account: o.account,
        // 원본도 비었거나 0 이면 1 로 올렸다 (sales_analysis_add.php:296).
        quantity: Math.max(1, Number(quantityOf(o)) || 0),
        memo,
        open_date: openDate !== '' ? openDate : (data.openDate ?? ''),
        end_flag: 0,
        end_date: '',
      }));
      return reportsApi.createSalesAnalysisRows(rows);
    },
    onSuccess: (r) => {
      setResult({ inserted: r.inserted, skipped: r.skipped });
      if (r.inserted === 0) return;
      // 표를 다시 받는다. 이 모달의 상품 조회도 같은 키 아래라 함께 새로
      // 오므로, 방금 넣은 옵션은 '등록됨' 으로 바뀐다 - 손으로 켜둔 체크는
      // 지워서 그 기본값(등록된 것은 꺼둠)이 다시 먹게 한다.
      setChecked({});
      onAdded();
    },
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">이벤트 상품 추가</h2>

        <div className="form-stack">
          <Field label="상품코드로 찾기" as="div">
            <div className="sales-analysis-search">
              <input
                className="input"
                value={draft}
                placeholder="예: 2493083"
                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    search();
                  }
                }}
              />
              <button type="button" className="btn btn--primary" onClick={search} disabled={draft.trim() === ''}>
                검색
              </button>
            </div>
          </Field>

          {goods.isFetching && <p className="hint">상품을 찾는 중…</p>}
          {goods.isError && (
            <div className="qty-msg qty-msg--error">
              {goods.error instanceof Error ? goods.error.message : '상품을 찾지 못했습니다.'}
            </div>
          )}
          {data && data.options.length === 0 && (
            <div className="qty-msg qty-msg--error">검색된 상품이 없습니다. (상품코드: {goodsIdx})</div>
          )}

          {data && data.options.length > 0 && (
            <>
              <div className="form-summary">
                <div className="form-summary__main">{data.gname}</div>
                <div className="form-summary__sub">
                  상품코드 {data.goodsIdx} · 옵션 {data.options.length}개
                  {data.eventType !== 0 &&
                    ` · ${EVENT_TYPES.find((t) => t.value === data.eventType)?.label ?? ''}`}
                </div>
              </div>

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '38px' }}>선택</th>
                      <th style={{ width: '54px' }}>이미지</th>
                      <th>옵션</th>
                      <th style={{ width: '120px' }}>바코드</th>
                      <th style={{ width: '96px', textAlign: 'right' }}>초기 재고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.options.map((o) => {
                      const key = optionKey(o);
                      return (
                        <tr key={key}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`${o.opName || o.barcode} 선택`}
                              checked={isChecked(o)}
                              onChange={(e) =>
                                setChecked((c) => ({ ...c, [key]: e.target.checked }))
                              }
                            />
                          </td>
                          <td>
                            {o.thumbnail ? <img className="thumb" src={o.thumbnail} alt="" loading="lazy" /> : '-'}
                          </td>
                          <td>
                            {o.opName || <span className="td-muted">옵션 없음</span>}
                            {o.registered && <span className="badge badge--danger">등록됨</span>}
                          </td>
                          <td className="td-muted">{o.barcode || '-'}</td>
                          <td className="td-right">
                            <input
                              className="input input--qty"
                              type="number"
                              min={1}
                              aria-label={`${o.opName || o.barcode} 초기 재고`}
                              value={quantityOf(o)}
                              onChange={(e) =>
                                setQuantities((q) => ({ ...q, [key]: e.target.value }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Field label="판매시작일">
                <input
                  className="input"
                  type="date"
                  value={openDate !== '' ? openDate : data.openDate}
                  onChange={(e) => setOpenDate(e.target.value)}
                />
              </Field>

              <Field label="메모 (등록하는 모든 행에 함께 들어갑니다)">
                <textarea className="input" rows={3} value={memo} onChange={(e) => setMemo(e.target.value)} />
              </Field>

              {save.isError && (
                <div className="qty-msg qty-msg--error">
                  {save.error instanceof Error ? save.error.message : '등록하지 못했습니다.'}
                </div>
              )}

              {result && (
                <div className="qty-msg">
                  {result.inserted}개 등록 완료
                  {result.skipped > 0 && ` (이미 등록된 ${result.skipped}개는 건너뜀)`}
                </div>
              )}

              <button
                type="button"
                className="btn btn--primary btn--block"
                disabled={selected.length === 0 || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? '등록 중…' : `선택한 ${selected.length}개 등록하기`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
